import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NO_ELEMENT, rm } from 'utils';
import { IconCheckmark } from 'components/Shared/Icon';
import { Checkbox } from 'components/Shared/Form';
import { cartDiscountRemove } from 'components/Cart/actions';
import Price from 'components/Shared/Price';
import { Voucher } from 'models/Voucher';
import {
  getDiscountCode,
  getTotalDiscountAmount,
  getTotalDiscountPercent,
  getVouchers
} from 'views/CheckoutPage/selector';
import styles from './discount.scss';
import cn from 'classnames';
import { Button } from 'components/Button';
import { IconCross } from 'components/Shared/Icon/themes/BGH-RD3/iconCross';
import Spinner from 'components/Shared/Loader';
import { useUpdateDiscount } from './useUpdateDiscount';

export const ActiveDiscounts = () => {
  const dispatch = useDispatch();
  const vouchers = useSelector(getVouchers);
  const discountCode = useSelector(getDiscountCode);
  const totalDiscountAmount = useSelector(getTotalDiscountAmount);
  const totalDiscountPercent = useSelector(getTotalDiscountPercent);

  const resetDiscount = (code: string) => dispatch(cartDiscountRemove(code));

  const hasDiscountCode = Boolean(discountCode && totalDiscountAmount);
  if (!vouchers.length && !hasDiscountCode) {
    return NO_ELEMENT;
  }

  return (
    <ul className={styles.activeDiscounts}>
      {hasDiscountCode && (
        <li className={styles.discountrow}>
          <ActiveDiscount
            discountCode={discountCode}
            totalDiscountAmount={totalDiscountAmount}
            totalDiscountPercent={totalDiscountPercent}
          />
        </li>
      )}
      {vouchers.map(voucher => (
        <li className={styles.discountrow} key={voucher.id}>
          <ActiveVoucher key={voucher.id} resetDiscount={resetDiscount} {...voucher} />
        </li>
      ))}
    </ul>
  );
};

interface ActiveVoucherProps extends Voucher {
  resetDiscount: (code: string) => void;
}
const ActiveVoucher = ({ amount, code, resetDiscount }: ActiveVoucherProps) => {
  const [removed, setRemoved] = useState(false);

  const onChange = () => {
    resetDiscount(code);
    setRemoved(true);
  };

  return (
    <div className={styles.activeDiscount}>
      <Checkbox
        onChange={onChange}
        checked={!removed}
        className={styles.check}
        appearance="secondary"
      />
      <div className={styles.discountCodeLabel}>
        <span>{rm('checkout_giftcard')}</span>
      </div>
      <div className={styles.savings}>
        - <Price price={amount} appearance="discount" />
      </div>
    </div>
  );
};

interface ActiveDiscountProps {
  discountCode: string;
  totalDiscountAmount: number;
  totalDiscountPercent: string;
}

const ActiveDiscount = ({
  discountCode,
  totalDiscountAmount,
  totalDiscountPercent
}: ActiveDiscountProps) => {
  const { submitDiscount, isLoading } = useUpdateDiscount();

  return (
    <div className={styles.activeDiscount}>
      <IconCheckmark size="small" className={cn(styles.check, styles.checkmark)} />
      <div className={styles.discountCodeLabel}>
        <span>{rm('checkout_discount_code')}</span> {discountCode}
      </div>
      <div className={styles.discountPercent}>-{totalDiscountPercent}</div>
      <div className={styles.savings}>
        {rm('checkout_giftcard_savings')}
        &nbsp;
        <Price price={totalDiscountAmount} appearance="discount" />!
      </div>
      <Button
        appearance="link"
        onClick={() => submitDiscount({ isDelete: true })}
        className={styles.removeButton}
      >
        <Spinner loading={isLoading} className={styles.spinner}>
          <IconCross />
        </Spinner>
      </Button>
    </div>
  );
};
